/**
 * Redux middleware for JSON Schema validation
 * Validates actions and state changes against schemas
 */

import { Middleware } from '@reduxjs/toolkit';
import {
  validateBudgetPositions,
  validateExternalPositions,
  validateCalculationFigures,
  validateUnitHoursData,
  validateBillingRatesData,
  validatePhases,
  validateHourData,
} from '../../utils/jsonSchemaValidation';

// Define action types that need validation
const ACTION_VALIDATORS: Record<string, (payload: any) => void> = {
  // Budget actions
  'budget/setBudgetPositions': (payload) => {
    const result = validateBudgetPositions(payload);
    if (!result.valid) {
      console.warn('❌ Invalid budget positions in Redux action:', result.errors);
    }
  },
  
  'budget/setExternalPositions': (payload) => {
    const result = validateExternalPositions(payload);
    if (!result.valid) {
      console.warn('❌ Invalid external positions in Redux action:', result.errors);
    }
  },
  
  'budget/setCalculationFigures': (payload) => {
    const result = validateCalculationFigures(payload);
    if (!result.valid) {
      console.warn('❌ Invalid calculation figures in Redux action:', result.errors);
    }
  },
  
  'budget/setUnitHoursData': (payload) => {
    const result = validateUnitHoursData(payload);
    if (!result.valid) {
      console.warn('❌ Invalid unit hours data in Redux action:', result.errors);
    }
  },
  
  'budget/setBillingRates': (payload) => {
    const result = validateBillingRatesData(payload);
    if (!result.valid) {
      console.warn('❌ Invalid billing rates in Redux action:', result.errors);
    }
  },
  
  'budget/setPhases': (payload) => {
    const result = validatePhases(payload);
    if (!result.valid) {
      console.warn('❌ Invalid phases in Redux action:', result.errors);
    }
  },
  
  'budget/updateHourData': (payload) => {
    if (payload && payload.hour_data) {
      const result = validateHourData(payload.hour_data);
      if (!result.valid) {
        console.warn('❌ Invalid hour data in Redux action:', result.errors);
      }
    }
  },
  
  // API response actions (from redux-thunk)
  'budget/fetchBudgetSuccess': (payload) => {
    if (payload.budget_positions) {
      const result = validateBudgetPositions(payload.budget_positions);
      if (!result.valid) {
        console.warn('❌ Invalid budget_positions from API:', result.errors);
      }
    }
    
    if (payload.external_positions) {
      const result = validateExternalPositions(payload.external_positions);
      if (!result.valid) {
        console.warn('❌ Invalid external_positions from API:', result.errors);
      }
    }
    
    if (payload.calculation_figures) {
      const result = validateCalculationFigures(payload.calculation_figures);
      if (!result.valid) {
        console.warn('❌ Invalid calculation_figures from API:', result.errors);
      }
    }
  },
};

/**
 * Redux middleware for schema validation
 */
export const schemaValidationMiddleware: Middleware = (store) => (next) => (action: any) => {
  // Check if this action needs validation
  if (action && typeof action === 'object' && 'type' in action) {
    const validator = ACTION_VALIDATORS[action.type];
    
    if (validator && action.payload) {
      try {
        validator(action.payload);
      } catch (error) {
        console.error('Error in schema validation middleware:', error);
      }
    }
  }
  
  // Continue with the action
  const result = next(action);
  
  // Validate state after action (in development only)
  if (process.env.NODE_ENV === 'development' && action && typeof action === 'object' && 'type' in action) {
    validateStateAfterAction(store.getState(), action);
  }
  
  return result;
};

/**
 * Validate specific parts of state after actions
 * This helps catch data corruption issues
 */
function validateStateAfterAction(state: any, action: any): void {
  // Only validate budget-related state changes
  if (!action.type.startsWith('budget/')) {
    return;
  }
  
  // Validate budget state if it exists
  if (state.budget) {
    const budget = state.budget;
    
    // Validate current budget data
    if (budget.currentBudget) {
      if (budget.currentBudget.budget_positions) {
        const result = validateBudgetPositions(budget.currentBudget.budget_positions);
        if (!result.valid) {
          console.warn('⚠️ Invalid budget_positions in Redux state after action:', action.type, result.errors);
        }
      }
      
      if (budget.currentBudget.external_positions) {
        const result = validateExternalPositions(budget.currentBudget.external_positions);
        if (!result.valid) {
          console.warn('⚠️ Invalid external_positions in Redux state after action:', action.type, result.errors);
        }
      }
    }
    
    // Validate budget list items
    if (budget.budgetList && Array.isArray(budget.budgetList)) {
      budget.budgetList.forEach((item: any, index: number) => {
        if (item.budget_positions) {
          const result = validateBudgetPositions(item.budget_positions);
          if (!result.valid) {
            console.warn(`⚠️ Invalid budget_positions in budgetList[${index}] after action:`, action.type, result.errors);
          }
        }
      });
    }
  }
  
  // Validate budgetDetail state if it exists
  if (state.budgetDetail && state.budgetDetail.budget_positions) {
    const result = validateBudgetPositions(state.budgetDetail.budget_positions);
    if (!result.valid) {
      console.warn('⚠️ Invalid budget_positions in budgetDetail state after action:', action.type, result.errors);
    }
  }
}

/**
 * Helper function to validate budget data before saving
 * Can be used in action creators
 */
export function validateBudgetData(budgetData: any): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (budgetData.budget_positions) {
    const result = validateBudgetPositions(budgetData.budget_positions);
    if (!result.valid && result.errors) {
      errors.push(...result.errors.map(e => `budget_positions: ${e.field} - ${e.message}`));
    }
  }
  
  if (budgetData.external_positions) {
    const result = validateExternalPositions(budgetData.external_positions);
    if (!result.valid && result.errors) {
      errors.push(...result.errors.map(e => `external_positions: ${e.field} - ${e.message}`));
    }
  }
  
  if (budgetData.calculation_figures) {
    const result = validateCalculationFigures(budgetData.calculation_figures);
    if (!result.valid && result.errors) {
      errors.push(...result.errors.map(e => `calculation_figures: ${e.field} - ${e.message}`));
    }
  }
  
  if (budgetData.unit_hours_data) {
    const result = validateUnitHoursData(budgetData.unit_hours_data);
    if (!result.valid && result.errors) {
      errors.push(...result.errors.map(e => `unit_hours_data: ${e.field} - ${e.message}`));
    }
  }
  
  if (budgetData.billing_rates_data) {
    const result = validateBillingRatesData(budgetData.billing_rates_data);
    if (!result.valid && result.errors) {
      errors.push(...result.errors.map(e => `billing_rates_data: ${e.field} - ${e.message}`));
    }
  }
  
  if (budgetData.phases) {
    const result = validatePhases(budgetData.phases);
    if (!result.valid && result.errors) {
      errors.push(...result.errors.map(e => `phases: ${e.field} - ${e.message}`));
    }
  }
  
  return {
    valid: errors.length === 0,
    errors
  };
}